<script setup lang="ts">
    import { ref, computed, watch, onMounted, toRef } from 'vue'
    import { guid } from "@common/Guid"
    import { formatStringForDateInput } from "@common/Common"

    import type { AlternativeEditorAttributeFieldReadModel } from "@Alternatives/Alternatives/Domain/readmodels"
    import type { ValidationError } from "@common/types"

    /* local VM */

    class ValueViewModel
    {
        Id: string = guid()
        Value: string | null = null
        Error: string | null = null

        constructor(value: any)
        {
            this.Value = formatStringForDateInput(value)
        }
    }

    /* props */

    const props = defineProps<{
        propF: AlternativeEditorAttributeFieldReadModel
        propErrors: ValidationError[]
    }>()

    /* shortcuts */

    const f = toRef(props, 'propF')
    const errors = toRef(props, 'propErrors')

    /* derived */

    const error = computed(() =>
        errors.value?.find(e =>
            e.Context.startsWith(`AlternativeAttribute:${f.value.Id}`)
        )?.Error
    )

    const minDate = computed(() => formatStringForDateInput(f.value.MinDate))
    const maxDate = computed(() => formatStringForDateInput(f.value.MaxDate))

    /* values */

    const values = ref<ValueViewModel[]>([])

    /* sync back to domain */

    watch(
        () => values.value,
        (vals) =>
        {
            f.value.Values = vals.map(v => v.Value)
        },
        { deep: true }
    )

    /* actions */

    function addValue()
    {
        values.value.push(new ValueViewModel(null))
    }

    function removeValue(v: ValueViewModel)
    {
        const index = values.value.indexOf(v)
        if (index >= 0)
        {
            values.value.splice(index, 1)
        }
    }

    /* init */

    onMounted(() =>
    {
        values.value = f.value.Values.map(v => new ValueViewModel(v))
    })
</script>

<template>
    <div class="form-group">
        <label>
            <span v-if="f.IsRequired" style="color: red;">*</span> {{ f.Name }}
            <span v-if="f.IsReadonly === false">
                (<a @click="addValue" class="text-primary">add</a>)
            </span>
        </label>

        <div v-for="v in values" :key="v.Id">
            <div class="input-group">
                <input type="date"
                       class="form-control"
                       placeholder="Date..."
                       title="Please enter date"
                       v-model="v.Value"
                       :readonly="f.IsReadonly === true"
                       :min="minDate"
                       :max="maxDate" />

                <div class="input-group-append" v-if="f.IsReadonly === false">
                    <button type="button"
                            class="btn btn-outline-secondary"
                            @click="removeValue(v)">
                        <span class="fa fa-times"></span>
                    </button>
                </div>
            </div>

            <div class="error">{{ v.Error }}</div>
        </div>

        <div class="error">{{ error }}</div>

        <small v-if="f.Description" class="form-text text-muted">
            {{ f.Description }}
        </small>
    </div>
</template>
